/* eslint-disable react-hooks/refs -- Refs in useMemo are intentional for structural sharing:
   we compare previous vs next values synchronously to preserve object identity and prevent
   unnecessary context consumer re-renders. Using useEffect would cause a render with stale data. */
import { createContext, useContext, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client/react';
import { useParams } from 'react-router-dom';
import { groupBy, keyBy, orderBy } from 'lodash/fp';
import { PROJECT_ANNOTATION_STATE } from '../../graphql/project';
import { Annotation, ProjectAnnotationStateQuery, ProjectAnnotationStateQueryVariables } from '../../types/graphqlTypes';
import { AnnotationContextType, isAnnotationContextGroup } from '../../contexts/AnnotationContext';

// ---------------------------------------------------------------------------
// Split contexts — consumers subscribe only to the slice they need.
// ---------------------------------------------------------------------------

/** Ordered tree of annotations/groups (used by the left-panel list). */
const AnnotationTreeContext = createContext<AnnotationContextType['annotations']>([]);
/** Flat lookup map keyed by identifier (used by right panel, menus, etc.). */
const AnnotationLookupContext = createContext<AnnotationContextType['annotationsByIdentifier']>({});
/** Calculations grouped by annotation identifier. */
const CalculationContext = createContext<AnnotationContextType['calculationsByIdentifier']>({});

/**
 * Provider component — renders once near the top of the tree.
 * Single useQuery subscription for annotation data. All consumers
 * read from React Context instead of creating their own subscriptions.
 */
export const AnnotationContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { projectId = '' } = useParams();
  const { data } = useQuery<ProjectAnnotationStateQuery, ProjectAnnotationStateQueryVariables>(PROJECT_ANNOTATION_STATE, {
    variables: { projectId },
    fetchPolicy: 'cache-only',
  });
  const project = data?.projectById;

  const prevCalcRef = useRef<AnnotationContextType['calculationsByIdentifier']>({});
  const calculationsByIdentifier = useMemo(() => {
    const next = groupBy('annotationIdentifier', project?.calculations || []);
    const prev = prevCalcRef.current;
    const nextKeys = Object.keys(next);
    const prevKeys = Object.keys(prev);
    if (nextKeys.length === prevKeys.length && nextKeys.every((k) => prev[k] === next[k])) {
      return prev;
    }
    prevCalcRef.current = next;
    return next;
  }, [project?.calculations]);

  const sanitizedAnnotations = useMemo(() => {
    const groupIds = new Set(project?.state?.groups?.map((g) => g.identifier) || []);
    return (project?.state?.annotations || []).map((a) => (a.groupIdentifier && !groupIds.has(a.groupIdentifier) ? { ...a, groupIdentifier: null } : a));
  }, [project?.state?.annotations, project?.state?.groups]);

  const prevLookupRef = useRef<AnnotationContextType['annotationsByIdentifier']>({});
  const annotationsByIdentifier = useMemo(() => {
    const next = keyBy('identifier', sanitizedAnnotations);
    const prev = prevLookupRef.current;
    const nextKeys = Object.keys(next);
    const prevKeys = Object.keys(prev);
    if (nextKeys.length === prevKeys.length && nextKeys.every((k) => prev[k] === next[k])) {
      return prev;
    }
    prevLookupRef.current = next;
    return next;
  }, [sanitizedAnnotations]);

  // Structural sharing: reuse previous tree items when the underlying data is unchanged.
  // This prevents downstream consumers from re-rendering when annotations didn't actually change.
  const prevTreeRef = useRef<AnnotationContextType['annotations']>([]);

  const annotations = useMemo(() => {
    const groups = project?.state.groups || [];
    const annotationsByGroupId = groupBy('groupIdentifier', sanitizedAnnotations);
    const groupsById = keyBy('identifier', groups);
    const orderedIds = orderBy('index', 'asc', project?.state.orderedIdentifiers);
    const prev = prevTreeRef.current;

    // Previous group nodes, root AND nested, for structural reuse.
    const prevByIdentifier = new Map(prev.map((item) => [item.identifier, item]));
    for (const item of prev) {
      if (!isAnnotationContextGroup(item)) continue;
      for (const child of item.annotations) {
        if (isAnnotationContextGroup(child)) prevByIdentifier.set(child.identifier, child);
      }
    }

    // Nested child groups per parent (a FLOOR group's ROOM folders). A group
    // whose parent no longer exists falls back to root rather than vanishing.
    const childGroupsByParent = new Map<string, typeof groups>();
    for (const g of groups) {
      if (g.parentIdentifier && groupsById[g.parentIdentifier]) {
        const list = childGroupsByParent.get(g.parentIdentifier) ?? [];
        list.push(g);
        childGroupsByParent.set(g.parentIdentifier, list);
      }
    }

    /**
     * Build a group node: its annotations merged with its nested child groups
     * in one sequence ordered by the group's ordering context. Returns the
     * previous node instance when nothing changed (structural sharing).
     */
    const buildGroupNode = (group: (typeof groups)[number]): AnnotationContextType['annotations'][number] => {
      const groupAnnotations = annotationsByGroupId[group.identifier] || [];
      const annotationsById = keyBy('identifier', groupAnnotations);
      const childGroupNodes = new Map((childGroupsByParent.get(group.identifier) ?? []).map((g) => [g.identifier, buildGroupNode(g)]));
      // Dedupe by identifier: transient cache states (failed optimistic
      // writes) can hold repeated ordered entries — render each child once.
      const seenChildIds = new Set<string>();
      const children = orderedIds
        .filter(({ identifier }) => {
          if (seenChildIds.has(identifier)) return false;
          if (!annotationsById[identifier] && !childGroupNodes.has(identifier)) return false;
          seenChildIds.add(identifier);
          return true;
        })
        .map((oi) => childGroupNodes.get(oi.identifier) ?? annotationsById[oi.identifier]);
      // Children missing an ordering entry (lost-update races on the ordering
      // rebuild) still render — appended at the end, never silently dropped:
      // an invisible group can't be deleted or reconciled by anyone.
      for (const [id, node] of childGroupNodes) {
        if (seenChildIds.has(id)) continue;
        seenChildIds.add(id);
        children.push(node);
      }
      for (const ann of groupAnnotations) {
        if (seenChildIds.has(ann.identifier)) continue;
        seenChildIds.add(ann.identifier);
        children.push(ann);
      }

      // Reuse previous group object if children and group data are identical
      const prevGroup = prevByIdentifier.get(group.identifier);
      if (prevGroup && isAnnotationContextGroup(prevGroup)) {
        const prevChildren = prevGroup.annotations;
        if (
          prevGroup.name === group.name &&
          prevGroup.type === group.type &&
          prevChildren.length === children.length &&
          prevChildren.every((c, i) => c === children[i])
        ) {
          return prevGroup;
        }
      }

      return { ...group, annotations: children };
    };

    const seenRootIds = new Set<string>();
    const result: AnnotationContextType['annotations'] = orderedIds.reduce(
      (acc, orderInfo) => {
        if (seenRootIds.has(orderInfo.identifier)) return acc;
        const annotation = annotationsByIdentifier[orderInfo.identifier];
        if ((annotation as Annotation)?.groupIdentifier) return acc;
        const group = groupsById[orderInfo.identifier];
        if (group) {
          // Nested groups render inside their parent, not at root.
          if (group.parentIdentifier && groupsById[group.parentIdentifier]) return acc;
          seenRootIds.add(orderInfo.identifier);
          return [...acc, buildGroupNode(group)];
        }
        if (!annotation) return acc;
        seenRootIds.add(orderInfo.identifier);
        return [...acc, annotation];
      },
      [] as AnnotationContextType['annotations'],
    );

    // Root items missing an ordering entry still render (same rationale as
    // the per-group append above).
    for (const g of groups) {
      if (seenRootIds.has(g.identifier)) continue;
      if (g.parentIdentifier && groupsById[g.parentIdentifier]) continue; // renders nested
      seenRootIds.add(g.identifier);
      result.push(buildGroupNode(g));
    }
    for (const a of sanitizedAnnotations) {
      if (seenRootIds.has(a.identifier) || a.groupIdentifier) continue;
      seenRootIds.add(a.identifier);
      result.push(a);
    }

    // If entire tree is structurally identical, reuse the previous reference
    if (
      result.length === prev.length &&
      result.every((item, i) => item === prev[i])
    ) {
      return prev;
    }

    prevTreeRef.current = result;
    return result;
  }, [annotationsByIdentifier, sanitizedAnnotations, project?.state.groups, project?.state.orderedIdentifiers]);

  return (
    <AnnotationTreeContext.Provider value={annotations}>
      <AnnotationLookupContext.Provider value={annotationsByIdentifier}>
        <CalculationContext.Provider value={calculationsByIdentifier}>
          {children}
        </CalculationContext.Provider>
      </AnnotationLookupContext.Provider>
    </AnnotationTreeContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Granular consumer hooks — prefer these over useAnnotationContext().
// ---------------------------------------------------------------------------

/** Ordered tree of annotations and groups (left-panel list, group menus). */
export const useAnnotationTree = (): AnnotationContextType['annotations'] => {
  return useContext(AnnotationTreeContext);
};

/** Flat lookup map keyed by identifier (right panel, individual lookups). */
export const useAnnotationLookup = (): AnnotationContextType['annotationsByIdentifier'] => {
  return useContext(AnnotationLookupContext);
};

/** Calculations grouped by annotation identifier. */
export const useCalculationsByIdentifier = (): AnnotationContextType['calculationsByIdentifier'] => {
  return useContext(CalculationContext);
};

// ---------------------------------------------------------------------------
// Backward-compatible hook — returns all three slices at once.
// Prefer the granular hooks above for new code.
// ---------------------------------------------------------------------------

/**
 * Consumer hook — reads from shared context. No useQuery, no Apollo subscription.
 * All consumers share a single subscription via AnnotationContextProvider.
 */
export const useAnnotationContext = (): AnnotationContextType => {
  const annotations = useContext(AnnotationTreeContext);
  const annotationsByIdentifier = useContext(AnnotationLookupContext);
  const calculationsByIdentifier = useContext(CalculationContext);
  return useMemo(
    () => ({ annotations, annotationsByIdentifier, calculationsByIdentifier }),
    [annotations, annotationsByIdentifier, calculationsByIdentifier],
  );
};
